import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import Layout from './Layout';
import HomePage from './pages/HomePage';
import { AuthProvider } from './auth/AuthContext';

/**
 * As telas chegam sob demanda, uma por vez.
 *
 * Enquanto os imports eram estáticos, o build saía num arquivo só de ~600 kB
 * (175 kB comprimidos), e quem abria a landing baixava junto o painel de admin,
 * o importador de backup, o caixa, o placar e o desenho em canvas do
 * compartilhar — tudo antes de ver a primeira palavra da página. No 4G do
 * campinho isso é a espera que o usuário descreveu como travamento.
 *
 * ⚠️ **A `HomePage` fica estática, e as outras não.** Ela é a rota `index` E a
 * curinga: é a primeira tela de quase toda visita, e a única cujo carregamento
 * ninguém pode esperar sem ver tela vazia. Pô-la num chunk à parte trocaria um
 * download grande por dois sequenciais — o `index.html`, depois o pedaço da
 * home —, que é pior exatamente no caso que se quer melhorar.
 *
 * A espera e a falha de cada pedaço são tratadas em `routing.tsx`, num
 * `Suspense` e num limite de erro que envolvem o `<Outlet />` do Layout.
 */
const NamesDrawPage = lazy(() => import('./pages/NamesDrawPage'));
const RosterPage = lazy(() => import('./pages/RosterPage'));
const DrawConfigPage = lazy(() => import('./pages/DrawConfigPage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const ScoreboardPage = lazy(() => import('./pages/ScoreboardPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));

const PeladaDetailPage = lazy(() => import('./pages/cloud/PeladaDetailPage'));
const DrawResultPage = lazy(() => import('./pages/cloud/DrawResultPage'));
const ManualDrawPage = lazy(() => import('./pages/cloud/ManualDrawPage'));
const AcceptInvitePage = lazy(() => import('./pages/cloud/AcceptInvitePage'));

const AccountPage = lazy(() => import('./pages/auth/AccountPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const ForgotPage = lazy(() => import('./pages/auth/ForgotPage'));
const VerifyPage = lazy(() => import('./pages/auth/VerifyPage'));
const ResetPage = lazy(() => import('./pages/auth/ResetPage'));

const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const CookiesPage = lazy(() => import('./pages/legal/CookiesPage'));
const DataDeletionPage = lazy(() => import('./pages/legal/DataDeletionPage'));

// Rotas reais, não estado interno: o Worker devolve o index.html para qualquer
// caminho (not_found_handling: single-page-application no wrangler.jsonc), então
// recarregar numa rota interna ou abrir um link compartilhado funciona.
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="nomes" element={<NamesDrawPage />} />
            <Route path="elenco" element={<RosterPage />} />
            <Route path="sortear" element={<DrawConfigPage />} />
            <Route path="times" element={<TeamsPage />} />
            <Route path="placar" element={<ScoreboardPage />} />
            <Route path="status" element={<StatusPage />} />

            <Route path="pelada/:peladaId" element={<PeladaDetailPage />} />
            {/* O resultado do sorteio tem endereço próprio: ele é o pico do
                produto e morava no fim da rolagem da aba, com o formulário que
                o produziu ainda ocupando a tela acima. O link é colável no
                grupo e é para onde o histórico aponta. */}
            <Route path="pelada/:peladaId/sorteio/:drawId" element={<DrawResultPage />} />
            {/* Montar os times à mão. Rota própria pelo mesmo motivo do
                resultado: escalar 15 pessoas é uma sessão de trabalho com
                rolagem e correção, e ela não cabe embaixo do formulário que a
                abriu. ⚠️ O caminho fica ANTES do curinga `:drawId` na ordem de
                leitura, mas o React Router casa por especificidade — `montar`
                literal ganha do parâmetro, então não existe sorteio com id
                "montar" capaz de sequestrar esta tela. */}
            <Route path="pelada/:peladaId/sorteio/montar" element={<ManualDrawPage />} />
            {/* `pelada/:id/sala/:roomId` existiu até 28/07 e caiu com a sala ao
                vivo. Links antigos colados em grupos de WhatsApp caem na rota
                curinga lá embaixo, que leva para casa em vez de quebrar. */}
            {/* Destino do link de convite — o caminho precisa bater com o
                montado em src/worker/peladas/routes.ts. */}
            <Route path="convite" element={<AcceptInvitePage />} />

            <Route path="conta" element={<AccountPage />} />
            {/* Sem link em lugar nenhum: a rota responde 404 para quem não é
                super-admin, e anunciá-la no menu não ajudaria ninguém. */}
            <Route path="admin" element={<AdminPage />} />
            {/* `/importar` era o endereço da tela antes de o painel existir, e
                está registrado no ESTADO.md. Continua funcionando, abrindo o
                painel já na aba de import. */}
            <Route path="importar" element={<Navigate to="/admin?aba=importar" replace />} />
            <Route path="entrar" element={<LoginPage />} />
            <Route path="criar-conta" element={<SignupPage />} />
            <Route path="esqueci-senha" element={<ForgotPage />} />
            {/* Destinos dos links enviados por email — os caminhos precisam
                bater com os montados em src/worker/auth/routes.ts. */}
            <Route path="verificar" element={<VerifyPage />} />
            <Route path="redefinir" element={<ResetPage />} />

            {/* Rotas em português: são o endereço de documento jurídico, e
                mudá-las depois quebraria links já publicados. */}
            <Route path="termos" element={<TermsPage />} />
            <Route path="privacidade" element={<PrivacyPage />} />
            <Route path="cookies" element={<CookiesPage />} />
            <Route path="exclusao-de-dados" element={<DataDeletionPage />} />

            <Route path="*" element={<HomePage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
